import React from "react";

// After a deploy, a long-lived tab may hold an index.html that references
// hashed chunks the new release removed — every route chunk import then fails
// until the page reloads. A sessionStorage flag limits recovery to one
// automatic reload, so a genuinely broken build can't cause a reload loop.
const RELOAD_FLAG_KEY = "xcf-stale-chunk-reload";

const isChunkLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Unable to preload CSS")
  );
};

/**
 * Reloads the page once per session. Returns true when the reload was
 * triggered, false when it already happened (or storage is unavailable,
 * where reloading would risk a loop).
 */
export const reloadOnce = (): boolean => {
  try {
    if (sessionStorage.getItem(RELOAD_FLAG_KEY)) {
      return false;
    }
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
  } catch {
    return false;
  }
  window.location.reload();
  return true;
};

export const reloadOnceForStaleChunk = (error: unknown): boolean => {
  return isChunkLoadError(error) && reloadOnce();
};

/**
 * React.lazy that recovers from stale-chunk failures with a one-shot page
 * reload instead of leaving the user on a permanently stuck spinner.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors React.lazy's own constraint
export const lazyWithRetry = <T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
): React.LazyExoticComponent<T> =>
  React.lazy(async () => {
    try {
      const module = await factory();
      try {
        // A successful load means the deployed chunks are reachable again —
        // re-arm the one-shot reload for the next deploy.
        sessionStorage.removeItem(RELOAD_FLAG_KEY);
      } catch {
        // Storage unavailable — nothing to re-arm.
      }
      return module;
    } catch (error) {
      if (reloadOnceForStaleChunk(error)) {
        // The page is reloading; keep the suspense fallback up meanwhile.
        return new Promise<{ default: T }>(() => undefined);
      }
      throw error;
    }
  });
